import { Injectable } from '@angular/core';
import { CanActivate, Router, UrlTree } from '@angular/router';
import { Observable, from, of } from 'rxjs';
import { switchMap, catchError } from 'rxjs/operators';
import { AuthService } from '../services/auth.service';

@Injectable({
  providedIn: 'root'
})
export class AuthGuard implements CanActivate {
  constructor(
    private readonly authService: AuthService,
    private readonly router: Router
  ) {}

  canActivate(): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    // Check if user is authenticated with either Okta or Cognito
    return from(this.authService.isAuthenticated()).pipe(
      switchMap(isOktaAuthenticated => {
        if (isOktaAuthenticated) {
          return of(true);
        }
        
        // If not authenticated with Okta, check Cognito
        return from(this.authService.isAuthenticated()).pipe(
          switchMap(isCognitoAuthenticated => {
            if (isCognitoAuthenticated) {
              return of(true);
            }
            
            // If neither is authenticated, redirect to login
            return of(this.router.createUrlTree(['/auth/login']));
          })
        );
      }),
      catchError(() => {
        // In case of any error, redirect to login
        return of(this.router.createUrlTree(['/auth/login']));
      })
    );
  }
}

// New guard to prevent authenticated users from accessing login pages
@Injectable({
  providedIn: 'root'
})
export class LoginGuard implements CanActivate {
  constructor(
    private readonly authService: AuthService,
    private readonly router: Router
  ) {}

  canActivate(): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    // Check if user is authenticated
    return from(this.authService.isAuthenticated()).pipe(
      switchMap(isAuthenticated => {
        // If user is authenticated, redirect them to dashboard
        if (isAuthenticated) {
          return of(this.router.createUrlTree(['/dashboard']));
        }
        
        // If user is not authenticated, allow access to login
        return of(true);
      }),
      catchError(() => {
        // In case of any error, allow access to login
        return of(true);
      })
    );
  }
}